import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useWebSocket — 실시간 시세 WebSocket hook
 *
 * [핵심 개선]
 * 기존: codes 변경 → url 변경 → WebSocket 재연결 (서버 부하 폭증)
 * 개선: url은 최초 1회만 연결, codes 변경 시 기존 ws에 update 메시지 전송
 *       → 서버 WebSocket 재연결 없이 구독 종목만 교체
 *
 * [재연결 정책]
 * - 정상 종료(onclose): 3초 후 재연결
 * - 오류(onerror): ws.close() → onclose 경유 재연결
 * - url 자체가 바뀔 때만 ws 재생성 (사실상 없어야 함)
 */
export default function useWebSocket(url) {
  const [tickData, setTickData] = useState(null);
  const wsRef      = useRef(null);
  const codesRef   = useRef([]);       // 현재 구독 중인 codes
  const pendingRef = useRef(null);     // 연결 전 도착한 codes (연결 후 즉시 전송)
  const reconnectTimer = useRef(null);

  // 구독 종목 업데이트 — ws가 열려있으면 즉시 전송, 아니면 pending
  const updateCodes = useCallback((codes) => {
    codesRef.current = codes;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'update_codes', codes }));
    } else {
      // 연결 중이면 pending에 저장 → onopen에서 전송
      pendingRef.current = codes;
    }
  }, []);

  useEffect(() => {
    if (!url) return;

    let destroyed = false;

    function connect() {
      if (destroyed) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // 연결 전에 요청된 codes가 있으면 즉시 전송
        if (pendingRef.current) {
          ws.send(JSON.stringify({ type: 'update_codes', codes: pendingRef.current }));
          pendingRef.current = null;
        } else if (codesRef.current.length > 0) {
          ws.send(JSON.stringify({ type: 'update_codes', codes: codesRef.current }));
        }
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'tick') setTickData(msg.data);
        } catch (_) {}
      };

      ws.onclose = () => {
        if (destroyed) return;
        // 3초 후 재연결
        reconnectTimer.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [url]);

  return { tickData, updateCodes };
}
